import type { CSSProperties } from 'react';
import { PrestazioneData } from 'utils/interfaces';
import { PRESTAZIONE_GROUPS, PrestazioneItemConfig } from '../prestazioneConfig';
import StaticCheckbox from './StaticCheckbox';

interface PrestazioneSectionProps {
  value: PrestazioneData;
  onChange: (next: PrestazioneData) => void;
  readOnly?: boolean;
  mobileLayout?: boolean;
}

const primaryLabelStyle: CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: '4px',
  fontSize: '13px',
  fontWeight: 400,
  color: '#1a2942',
  textTransform: 'uppercase',
  flex: '1 1 auto',
  minWidth: 0,
};

const childLabelStyle: CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  flexWrap: 'wrap',
  gap: '4px',
  fontSize: '12.5px',
  color: '#1a2942',
  textTransform: 'uppercase',
};

const inlineInputStyle: CSSProperties = {
  fontSize: '12.5px',
  color: '#1a2942',
  border: 'none',
  borderBottom: '1px solid #94a3b8',
  background: 'transparent',
  padding: '0 2px',
  minWidth: '40px',
};

export default function PrestazioneSection({ value, onChange, readOnly, mobileLayout }: PrestazioneSectionProps) {
  const setBool = (key: keyof PrestazioneData, checked: boolean) => {
    onChange({ ...value, [key]: checked });
  };

  const setText = (key: keyof PrestazioneData, text: string) => {
    onChange({ ...value, [key]: text === '' ? null : text });
  };

  const renderCheckbox = (checked: boolean, onToggle: () => void) =>
    readOnly ? (
      <StaticCheckbox checked={checked} />
    ) : (
      <input
        type="checkbox"
        checked={checked}
        onChange={onToggle}
        style={{ width: '11px', height: '11px', flexShrink: 0 }}
      />
    );

  const renderTextInput = (item: PrestazioneItemConfig, extraStyle?: CSSProperties) => {
    if (!item.textKey) return null;
    const textKey = item.textKey;
    return (
      <input
        type="text"
        value={(value[textKey] as string | null) || ''}
        disabled={readOnly}
        placeholder={item.textPlaceholder}
        onChange={e => setText(textKey, e.target.value)}
        style={{ ...inlineInputStyle, ...extraStyle }}
      />
    );
  };

  return (
    <div style={{ border: '1px solid #94a3b8' }}>
      <div style={{ fontSize: '13px', fontWeight: 800, color: '#1a2942', textTransform: 'uppercase', letterSpacing: '0.6px', padding: '3px 6px', background: '#eef2f7', borderBottom: '1px solid #94a3b8' }}>
        Prestazione
      </div>
      {PRESTAZIONE_GROUPS.map(group => {
        // Flat checklist groups (CER codes, Consegna/Pulizia WC/Ritiro): no parent/child split, every
        // item is an equal compact checkbox wrapped in one row, matching the paper form's flat lists.
        if (group.flatList) {
          return (
            <div
              key={group.title}
              style={{ display: 'flex', flexWrap: 'wrap', padding: '2px 6px', borderBottom: '1px solid #cbd5e1', gap: '2px 16px' }}
            >
              {group.items.map((item, idx) => (
                <label key={`${item.key}-${item.textKey || idx}`} style={childLabelStyle}>
                  {!item.noCheckbox && renderCheckbox(!!value[item.key], () => setBool(item.key, !value[item.key]))}
                  <span>{item.label}</span>
                  {renderTextInput(item, { minWidth: '50px' })}
                </label>
              ))}
            </div>
          );
        }

        // Every other group: first item is printed on its own line, at the same level as the rest
        // (no bold/heading treatment); the rest are either flush-right fields on that same line
        // (alignEnd, e.g. "CER ___" / "FIR ___") or indented sub-options on the line below.
        const [primary, ...rest] = group.items;
        const alignEndItems = rest.filter(i => i.alignEnd);
        const childItems = rest.filter(i => !i.alignEnd);

        return (
          <div key={group.title} style={{ padding: '2px 6px', borderBottom: '1px solid #cbd5e1' }}>
            <div
              style={{
                display: 'flex',
                flexDirection: mobileLayout ? 'column' : 'row',
                alignItems: mobileLayout ? 'flex-start' : 'center',
                justifyContent: 'space-between',
                flexWrap: 'wrap',
                gap: '4px 10px',
              }}
            >
              <label style={primaryLabelStyle}>
                {renderCheckbox(!!value[primary.key], () => setBool(primary.key, !value[primary.key]))}
                <span>{primary.label}</span>
                {renderTextInput(primary, { flex: 1, minWidth: '80px', fontWeight: 400, textTransform: 'none' })}
              </label>
              {alignEndItems.length > 0 && (
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '10px', flexShrink: 0 }}>
                  {alignEndItems.map((item, idx) => (
                    <label key={`${item.key}-${item.textKey || idx}`} style={childLabelStyle}>
                      <span>{item.label}</span>
                      {renderTextInput(item)}
                    </label>
                  ))}
                </div>
              )}
            </div>
            {childItems.length > 0 && (
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '2px 16px', paddingLeft: '16px', marginTop: '1px' }}>
                {childItems.map((item, idx) => (
                  <label key={`${item.key}-${item.textKey || idx}`} style={childLabelStyle}>
                    {!item.noCheckbox && renderCheckbox(!!value[item.key], () => setBool(item.key, !value[item.key]))}
                    <span>{item.label}</span>
                    {renderTextInput(item)}
                  </label>
                ))}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}
